import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  // Grid layout so the icon lines up with the first line of text (single- or
  // multi-line) instead of being absolutely pinned; icon is size-constrained and
  // nudged to sit on the text baseline.
  //
  // Column 1 is the icon track and collapses to 0 without one, so anything that
  // lands there is squeezed to zero width and wraps one character per line. The
  // `:not(svg)` rule keeps every element child out of it — AlertTitle and
  // AlertDescription already say col-start-2, and this covers the plain
  // wrappers callers pass. Bare text nodes cannot be selected in CSS at all, so
  // Alert wraps those below. Both are inert once a caller overrides `grid` with
  // a flex display.
  "relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[1.25rem_1fr] has-[>svg]:gap-x-3 [&>*:not(svg)]:col-start-2 [&>svg]:size-5 [&>svg]:translate-y-px [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/50 bg-destructive/5 text-destructive dark:border-destructive [&>svg]:text-destructive",
        success:
          "border-success/40 bg-success/5 text-foreground [&>svg]:text-success",
        warning:
          "border-warning/40 bg-warning/5 text-foreground [&>svg]:text-warning",
        info: "border-primary/30 bg-primary/5 text-foreground [&>svg]:text-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, children, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  >
    {/*
      A string child is an anonymous grid item: no element, so no class can move
      it out of the collapsed icon column. Wrapping it here means
      `<Alert>{message}</Alert>` renders as prose instead of a one-character-wide
      column, without every caller having to remember AlertDescription.
    */}
    {React.Children.map(children, (child) =>
      typeof child === "string" || typeof child === "number" ? (
        <div className="col-start-2 text-sm">{child}</div>
      ) : (
        child
      ),
    )}
  </div>
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  // children is rendered explicitly rather than spread, so the a11y lint can
  // see the heading has content.
  <h5
    ref={ref}
    className={cn("col-start-2 mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  >
    {children}
  </h5>
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("col-start-2 text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
